// Bake an {@link Interaction} into a flat, static {@link Part} list — the model
// posed with its self tracks, plus the actor placed at the station and posed
// with its root + rig tracks. This is the environment-agnostic seam that lets a
// renderer *show* an interaction (the headless preview and the tests use it
// today; a live viewer could too): it is the interaction analogue of
// `buildModel`, turning the two-body animation at one instant into geometry.
//
// Geometry is pre-transformed (each part's geometry is cloned and the composed
// world matrix applied) and the parts are returned with identity transforms, so
// `buildModel` re-derives flat shading in the final pose — no shearing from
// composing a node transform onto a part's own non-uniform scale.

import * as THREE from "three";
import type { Animation, Interaction, Model, Part, Vec3 } from "./types";
import { partMatrix } from "./build-model";
import { sampleAnimation, sampleInteraction, type NodeTransform } from "./animate";
import { ACTOR_ROOT } from "./rig";

const IDENTITY = new THREE.Matrix4();

/** The 4x4 a node transform represents about its pivot — matching how
 *  `instantiateModel` positions an animated node: T(pivot+pos)·R·S·T(-pivot). */
function nodeMatrix(tf: NodeTransform, pivot: Vec3): THREE.Matrix4 {
  const m = new THREE.Matrix4().makeTranslation(
    pivot[0] + tf.position[0],
    pivot[1] + tf.position[1],
    pivot[2] + tf.position[2],
  );
  m.multiply(new THREE.Matrix4().makeRotationFromEuler(
    new THREE.Euler(tf.rotation[0], tf.rotation[1], tf.rotation[2]),
  ));
  m.multiply(new THREE.Matrix4().makeScale(tf.scale[0], tf.scale[1], tf.scale[2]));
  m.multiply(new THREE.Matrix4().makeTranslation(-pivot[0], -pivot[1], -pivot[2]));
  return m;
}

/** The pivot an interaction track declares for `target`/`subject` (origin if none). */
function pivotFor(it: Interaction, target: string, subject: "self" | "actor"): Vec3 {
  for (const tr of it.tracks) {
    if ((tr.subject ?? "self") === subject && tr.target === target && tr.pivot) return tr.pivot;
  }
  return [0, 0, 0];
}

/** Clone a part's geometry into world space, returning a transform-free part so
 *  `buildModel` shades it in its final pose. */
function bakedPart(part: Part, world: THREE.Matrix4): Part {
  const m = world.clone().multiply(partMatrix(part));
  return {
    geometry: part.geometry.clone().applyMatrix4(m),
    color: part.color,
    ...(part.slot !== undefined ? { slot: part.slot } : {}),
  };
}

function variantParts(model: Model, variantId: string | undefined): Part[] {
  const v =
    (variantId ? model.variants.find((x) => x.id === variantId) : undefined) ??
    model.variants.find((x) => x.id === model.defaultVariantId) ??
    model.variants[0]!;
  return v.parts();
}

export interface BakeOptions {
  /** Which variant of the model to show (default: its default variant). */
  variantId?: string;
  /** Which variant of the actor to use (default: the actor's default variant). */
  actorVariantId?: string;
  /** Time in seconds into the interaction (default 0). */
  t?: number;
}

/**
 * Flatten `interaction` at time `t` into one {@link Part} list: the model's
 * parts (its self tracks applied to the named ones) plus `actor`'s parts placed
 * at the interaction's {@link Station} (the `"root"` track moving the whole
 * actor, other rig tracks moving a limb). Throws a teaching error if the
 * interaction references a station the model does not define. Pure and
 * three.js-only — no scene graph.
 */
export function bakeInteraction(
  model: Model,
  actor: Model,
  interaction: Interaction,
  opts: BakeOptions = {},
): Part[] {
  const station = model.stations.find((s) => s.id === interaction.station);
  if (!station) {
    throw new Error(
      `Interaction "${interaction.id}" references unknown station "${interaction.station}". ` +
      `Known stations: ${model.stations.map((s) => s.id).join(", ") || "(none)"}.`,
    );
  }
  const sampled = sampleInteraction(interaction, opts.t ?? 0);
  const out: Part[] = [];

  // The model itself: self tracks pose their named parts; everything else rests.
  for (const p of variantParts(model, opts.variantId)) {
    const tf = p.name ? sampled.self.get(p.name) : undefined;
    out.push(bakedPart(p, tf ? nodeMatrix(tf, pivotFor(interaction, p.name!, "self")) : IDENTITY));
  }

  // The actor: placed at the station, the "root" track composed onto that
  // placement, and any limb track applied in the actor's own local frame.
  const base = new THREE.Matrix4()
    .makeTranslation(station.position[0], station.position[1], station.position[2])
    .multiply(new THREE.Matrix4().makeRotationY(station.facing ?? 0));
  const rootTf = sampled.actor.get(ACTOR_ROOT);
  if (rootTf) base.multiply(nodeMatrix(rootTf, pivotFor(interaction, ACTOR_ROOT, "actor")));
  for (const p of variantParts(actor, opts.actorVariantId)) {
    const tf = p.name && p.name !== ACTOR_ROOT ? sampled.actor.get(p.name) : undefined;
    const world = tf ? base.clone().multiply(nodeMatrix(tf, pivotFor(interaction, p.name!, "actor"))) : base;
    out.push(bakedPart(p, world));
  }
  return out;
}

/** The pivot an animation track declares for `target` (origin if none). */
function animPivotFor(anim: Animation, target: string): Vec3 {
  for (const tr of anim.tracks) {
    if (tr.target === target && tr.pivot) return tr.pivot;
  }
  return [0, 0, 0];
}

export interface BakeAnimationOptions {
  /** Which variant of the model to pose (default: its default variant). */
  variantId?: string;
  /** Time in seconds into the animation (default 0). */
  t?: number;
}

/**
 * Flatten a plain {@link Animation} at time `t` into one static {@link Part}
 * list: the model's variant parts with each named part posed by the track that
 * drives it, everything else at rest. The single-body counterpart to
 * {@link bakeInteraction} (no actor) and the animation analogue of
 * {@link buildModel} — the pure seam a headless renderer or test uses to *show*
 * an animation at one instant. Loops or clamps per {@link Animation.loop}.
 */
export function bakeAnimation(
  model: Model,
  animation: Animation,
  opts: BakeAnimationOptions = {},
): Part[] {
  const sampled = sampleAnimation(animation, opts.t ?? 0);
  const out: Part[] = [];
  for (const p of variantParts(model, opts.variantId)) {
    const tf = p.name ? sampled.get(p.name) : undefined;
    out.push(bakedPart(p, tf ? nodeMatrix(tf, animPivotFor(animation, p.name!)) : IDENTITY));
  }
  return out;
}
